import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout/AppLayout';
import { useAppData } from '../../context/AppDataContext';
import { buildDisplayLanguages } from '../Settings/lists/bookLanguageFormSupport';
import {
  settingsListApiError,
} from '../Settings/lists/singleTableListSupport';
import { createBook, prepareBookDraftForApiUpload } from './bookApi';
import BookForm, {
  bookDraftCompleteForServerSave,
  bookDraftToCreateApiPayload,
  clearBookCreateDraftStorage,
  emptyBookFormDraft,
  loadBookCreateDraftFromStorage,
  saveBookCreateDraftToStorage,
  validateBookFormDraftDetailed,
  type BookFormDraft,
} from './BookForm';
import BookPreview from './BookPreview';
import BookSaveFullscreenLoading from './BookSaveFullscreenLoading';

const DRAFT_SAVE_DEBOUNCE_MS = 400;

export default function BookCreatePage() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<BookFormDraft>(() => loadBookCreateDraftFromStorage() ?? emptyBookFormDraft());
  const [formError, setFormError] = useState<string | null>(null);
  const [browserDraftNotice, setBrowserDraftNotice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrorStep, setValidationErrorStep] = useState<number | null>(null);
  const [bookPreviewOpen, setBookPreviewOpen] = useState(false);
  const saveDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    bookLanguages: fetchedBookLangs,
    authors,
    categories,
    ageGroups,
    loading: appDataLoading,
    error: appDataError,
  } = useAppData();
  const bookFormLanguages = useMemo(() => buildDisplayLanguages(fetchedBookLangs), [fetchedBookLangs]);

  const flushSaveDraftTimer = useCallback(() => {
    if (saveDraftTimerRef.current != null) {
      clearTimeout(saveDraftTimerRef.current);
      saveDraftTimerRef.current = null;
    }
  }, []);

  const handleDraftChange = useCallback(
    (next: BookFormDraft) => {
      setValidationErrorStep(null);
      setDraft(next);
      if (next.mode !== 'create') return;
      flushSaveDraftTimer();
      saveDraftTimerRef.current = setTimeout(() => {
        saveBookCreateDraftToStorage(next);
        saveDraftTimerRef.current = null;
      }, DRAFT_SAVE_DEBOUNCE_MS);
    },
    [flushSaveDraftTimer],
  );

  const handleClearPersistedDraft = useCallback(() => {
    flushSaveDraftTimer();
    setDraft(emptyBookFormDraft());
  }, [flushSaveDraftTimer]);

  const referenceLoading = appDataLoading;
  const referenceError = appDataError;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setBrowserDraftNotice(false);
    setValidationErrorStep(null);

    const validationFail = validateBookFormDraftDetailed(draft, {
      displayBookLanguages: bookFormLanguages,
    });
    if (validationFail) {
      setFormError(validationFail.message);
      setValidationErrorStep(validationFail.stepIndex);
      return;
    }

    if (!bookDraftCompleteForServerSave(draft)) {
      flushSaveDraftTimer();
      saveBookCreateDraftToStorage(draft, { force: true });
      setBrowserDraftNotice(true);
      return;
    }
    setSubmitting(true);
    try {
      const withPaths = await prepareBookDraftForApiUpload(draft);
      const payload = bookDraftToCreateApiPayload(withPaths);
      const { id } = await createBook(payload);
      setBrowserDraftNotice(false);
      setValidationErrorStep(null);
      flushSaveDraftTimer();
      clearBookCreateDraftStorage();
      navigate(`/library/${encodeURIComponent(id)}`, { replace: true });
    } catch (err: unknown) {
      setFormError(settingsListApiError(err, 'Ном хадгалахад алдаа гарлаа.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <BookSaveFullscreenLoading open={submitting} />
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          flex: 1,
          py: 4,
          px: { xs: 2, sm: 4 },
          mx: 'auto',
          width: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 0.5 }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
            Шинэ ном
          </Typography>
          <Tooltip title="Урьдчилан харах">
            <Button
              type="button"
              variant="text"
              color="primary"
              size="small"
              startIcon={<MenuBookOutlined />}
              onClick={() => setBookPreviewOpen(true)}
              aria-label="Урьдчилан харах"
              sx={{ textTransform: 'none', minWidth: 0 }}
            >
              Урьдчилан харах
            </Button>
          </Tooltip>
        </Stack>
        <BookPreview
          open={bookPreviewOpen}
          onClose={() => setBookPreviewOpen(false)}
          draft={draft}
          authors={authors}
          languages={bookFormLanguages}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
          Номын мэдээллийг оруулна уу
        </Typography>

        {formError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        ) : null}

        {browserDraftNotice ? (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setBrowserDraftNotice(false)}>
            Ноорог зөвхөн энэ вэб хөтчийн localStorage-д хадгалагдлаа (сервер рүү илгээгдээгүй). Зохиолч, нүүр
            зураг, ангилал, насны ангилал, хэлийн language_map бүрэн бөглөгдсөний дараа дахин «Хадгалах» дарвал
            серверт хадгална. Хөтөчийн өгөгдөл цэвэрлэгдэх эсвэл төхөөрөмж солигдох үед энэ ноорог алдагдаж болзошгүй.
          </Alert>
        ) : null}

        <BookForm
          draft={draft}
          onChange={handleDraftChange}
          languages={bookFormLanguages}
          onClearPersistedDraft={handleClearPersistedDraft}
          authors={authors}
          categories={categories}
          ageGroups={ageGroups}
          referenceLoading={referenceLoading}
          referenceError={referenceError}
          hideBookPreviewChrome
          validationErrorStep={validationErrorStep}
        />

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button type="submit" variant="contained" disabled={referenceLoading || submitting}>
            {submitting ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </Stack>
      </Box>
    </AppLayout>
  );
}
